import React, { useEffect } from 'react';
import { Form, Input, Select, Row, Col } from 'antd';
import type { UserRole } from './UserFormModal';
import { withDevModalFilePath, EmailInput } from '../common';

const { Modal } = withDevModalFilePath('client/src/components/user/UserInviteModal.tsx');
const { Option } = Select;
const { TextArea } = Input;

export interface UserInviteValues {
  email: string;
  name?: string;
  role: UserRole;
  department?: string;
  position?: string;
  message?: string;
}

interface UserInviteModalProps {
  visible: boolean;
  onCancel: () => void;
  onSubmit: (values: UserInviteValues) => Promise<void>;
  loading?: boolean;
  currentUserRole: UserRole;
}

const UserInviteModal: React.FC<UserInviteModalProps> = ({
  visible,
  onCancel,
  onSubmit,
  loading = false,
  currentUserRole,
}) => {
  const [form] = Form.useForm<UserInviteValues>();
  const isAdmin = currentUserRole === 'admin';

  useEffect(() => {
    if (visible) {
      form.resetFields();
    }
  }, [visible, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      await onSubmit(values);
    } catch (error) {
      console.error('Form validation failed:', error);
    }
  };

  const getAvailableRoles = () => {
    if (isAdmin) {
      return [
        { value: 'admin', label: '管理者' },
        { value: 'manager', label: 'マネージャー' },
        { value: 'user', label: 'ユーザー' },
        { value: 'viewer', label: '閲覧者' },
      ];
    } else {
      // マネージャーは管理者を招待できない
      return [
        { value: 'manager', label: 'マネージャー' },
        { value: 'user', label: 'ユーザー' },
        { value: 'viewer', label: '閲覧者' },
      ];
    }
  };

  return (
    <Modal
      title="外部協力会社を招待"
      open={visible}
      onCancel={onCancel}
      onOk={handleSubmit}
      confirmLoading={loading}
      width={600}
      okText="招待メールを送信"
      cancelText="キャンセル"
    >
      <div className="mb-4 p-4 bg-blue-50 rounded-lg">
        <p className="text-sm text-blue-700">
          外部協力会社の担当者に招待メールを送信します。受信者は招待リンクからアカウントを作成できます。
        </p>
      </div>

      <Form
        form={form}
        layout="vertical"
        initialValues={{
          role: 'user',
        }}
      >
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name="email"
              label="メールアドレス"
              rules={[
                { required: true, message: 'メールアドレスを入力してください' },
                { type: 'email', message: '有効なメールアドレスを入力してください' },
              ]}
            >
              <EmailInput placeholder="yamada@example.com" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name="name"
              label="氏名（任意）"
              rules={[
                { max: 100, message: '氏名は100文字以内で入力してください' },
              ]}
            >
              <Input placeholder="山田 太郎" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="role"
              label="権限"
              rules={[{ required: true, message: '権限を選択してください' }]}
            >
              <Select placeholder="権限を選択">
                {getAvailableRoles().map(role => (
                  <Option key={role.value} value={role.value}>
                    {role.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="department"
              label="部署（任意）"
              rules={[
                { max: 100, message: '部署名は100文字以内で入力してください' },
              ]}
            >
              <Input placeholder="開発部" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name="position"
              label="役職（任意）"
              rules={[
                { max: 100, message: '役職は100文字以内で入力してください' },
              ]}
            >
              <Input placeholder="主任" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name="message"
              label="招待メッセージ（任意）"
              rules={[
                { max: 500, message: 'メッセージは500文字以内で入力してください' },
              ]}
            >
              <TextArea
                placeholder="チームに参加していただきありがとうございます。"
                rows={4}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default UserInviteModal;
